import {
  KoButton,
  KoForm,
  KoModal,
  KoNano,
  KoPagination,
  KoTable,
  KoText,
  KoTitle,
  KoToast,
  KoTooltips,
  KoTag,
  KoDateFormatter
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import {
  COLUMNS_RELEASES,
  RELEASE_TEAM_OPTIONS,
  LIFECYCLE_STAGE_MAP,
  RELEASE_CLASSIFICATION,
  RISK_LEVEL_OPTIONS,
  DEFAULT_RELEASE_FLOW
} from '../constants';
import ReleaseCrud from './crud/ReleaseCrud';
import styles from './releases.module.scss';

const MOCK_RELEASES = [
  {
    releaseId: 'REL-001',
    name: 'Platform Upgrade',
    version: 'v4.2.0',
    classification: 'major',
    participatingTeams: [
      { team: 'api', branch: 'release/api-v3.0' },
      { team: 'web_app', branch: 'release/ui-v4.0' },
      { team: 'backend', branch: 'release/backend-v2.5' }
    ],
    environment: 'qa',
    stage: 'in_qa',
    qaStatus: 'in_progress',
    uatStatus: null,
    riskLevel: 'high',
    targetDate: '2026-03-15',
    owner: 'John Doe',
    releaseFlow: DEFAULT_RELEASE_FLOW
  },
  {
    releaseId: 'REL-002',
    name: 'Auth Redesign',
    version: 'v4.1.3',
    classification: 'minor',
    participatingTeams: [
      { team: 'web_app', branch: 'release/auth-ui-v4.1' },
      { team: 'backend', branch: 'release/auth-api-v4.1' }
    ],
    environment: 'uat',
    stage: 'in_uat',
    qaStatus: 'passed',
    uatStatus: 'pending',
    riskLevel: 'medium',
    targetDate: '2026-03-10',
    owner: 'Alice Park',
    releaseFlow: DEFAULT_RELEASE_FLOW
  },
  {
    releaseId: 'REL-003',
    name: 'Security Patch',
    version: 'v4.1.2',
    classification: 'patch',
    participatingTeams: [
      { team: 'devops', branch: 'release/infra-v4.1.2' },
      { team: 'backend', branch: 'release/sec-v4.1.2' }
    ],
    environment: 'production',
    stage: 'closed',
    qaStatus: 'passed',
    uatStatus: 'signed_off',
    riskLevel: 'low',
    targetDate: '2026-02-28',
    owner: 'Bob Chen',
    releaseFlow: DEFAULT_RELEASE_FLOW
  },
  {
    releaseId: 'REL-004',
    name: 'Mobile Push Notifications',
    version: 'v4.3.0',
    classification: 'minor',
    participatingTeams: [
      { team: 'mobile_app', branch: 'release/mobile-push-v4.3' },
      { team: 'api', branch: 'release/push-api-v4.3' }
    ],
    environment: 'development',
    stage: 'in_development',
    qaStatus: null,
    uatStatus: null,
    riskLevel: 'medium',
    targetDate: '2026-04-01',
    owner: 'Sarah Kim',
    releaseFlow: DEFAULT_RELEASE_FLOW
  },
  {
    releaseId: 'REL-005',
    name: 'Hotfix: Payment Gateway',
    version: 'v4.1.4',
    classification: 'hotfix',
    participatingTeams: [
      { team: 'backend', branch: 'hotfix/payment-v4.1.4' },
      { team: 'devops', branch: 'hotfix/deploy-v4.1.4' }
    ],
    environment: 'production',
    stage: 'deploying',
    qaStatus: 'passed',
    uatStatus: 'signed_off',
    riskLevel: 'critical',
    targetDate: '2026-02-24',
    owner: 'John Doe',
    releaseFlow: DEFAULT_RELEASE_FLOW
  },
  {
    releaseId: 'REL-006',
    name: 'Dashboard Analytics V2',
    version: 'v5.0.0',
    classification: 'major',
    participatingTeams: [
      { team: 'web_app', branch: 'release/dash-v5.0' },
      { team: 'data', branch: 'release/analytics-v5.0' },
      { team: 'api', branch: 'release/metrics-api-v5.0' }
    ],
    environment: 'development',
    stage: 'draft',
    qaStatus: null,
    uatStatus: null,
    riskLevel: 'high',
    targetDate: '2026-05-01',
    owner: 'Alice Park',
    releaseFlow: DEFAULT_RELEASE_FLOW
  }
];

function ReleaseList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  const [rowList, setRowList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isOpen, setModal] = useState(false);
  const [isOpenDelete, setDeleteModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState();
  const [, setSelectedRows] = useState([]);
  const [pagination, setPagination] = useState({ size: 20, offset: 0, page: 1 });
  const [form] = KoForm.useForm();

  const releaseBasePath = workspaceId
    ? `/workspace/${workspaceId}/development/release`
    : '/release';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'Releases', path: releaseBasePath }
    ]));
    setRowList(MOCK_RELEASES);
    setTotalCount(MOCK_RELEASES.length);
  }, [workspaceId]);

  const onSearchText = (text) => {
    if (!text) {
      setRowList(MOCK_RELEASES);
      return;
    }
    const q = text.toLowerCase();
    setRowList(MOCK_RELEASES.filter((r) =>
      r.version.toLowerCase().includes(q) ||
      r.name.toLowerCase().includes(q) ||
      r.owner.toLowerCase().includes(q)
    ));
  };

  const onPagination = (page, size) => {
    setPagination({ ...pagination, size, page, offset: (page - 1) * size });
  };

  const handleFinish = (values) => {
    if (!currentRecord?.releaseId) {
      values['releaseId'] = KoNano('REL');
      values['stage'] = 'draft';
    }
    setRowList((prev) => {
      if (currentRecord?.releaseId) {
        return prev.map((r) => r.releaseId === currentRecord.releaseId ? { ...r, ...values } : r);
      }
      return [...prev, values];
    });
    setTotalCount((prev) => currentRecord?.releaseId ? prev : prev + 1);
    setModal(false);
    form.resetFields();
    setCurrentRecord();
    KoToast.success(`Release ${currentRecord?.releaseId ? 'updated' : 'created'} successfully!`);
  };

  const handleDelete = () => {
    setRowList((prev) => prev.filter((r) => r.releaseId !== currentRecord?.releaseId));
    setTotalCount((prev) => prev - 1);
    setDeleteModal(false);
    setCurrentRecord();
    KoToast.success('Release deleted successfully!');
  };

  const getTeamLabel = (value) =>
    RELEASE_TEAM_OPTIONS.find((o) => o.value === value)?.label || value;

  const getClassificationConfig = (val) =>
    RELEASE_CLASSIFICATION.find((o) => o.value === val) || { label: val, color: 'default' };

  const getRiskConfig = (val) =>
    RISK_LEVEL_OPTIONS.find((o) => o.value === val) || { label: val, color: 'default' };

  const columnsLeft = [{
    title: 'Version',
    dataIndex: 'version',
    key: 'version',
    fixed: 'left',
    width: 120,
    render: (text) => (
      <div className={styles.versionCell}>
        <KoIcon name='BranchOutlined' width={12} height={12} />
        <KoText className={styles.versionText} text={text} />
      </div>
    )
  }];

  const columnsRight = [{
    title: '',
    dataIndex: 'x',
    key: 'x',
    fixed: 'right',
    width: 120,
    render: (_, record) => (
      <div className={`${styles.actionCell} tableBtn columnsRight`}
        onClick={() => setCurrentRecord(record)}>
        <KoTooltips title='Open'>
          <KoButton
            className='btn'
            iconBtn={true}
            icon={<KoIcon width={14} height={14} name='ArrowRightOutlined' />}
            onClick={() => navigate(`${releaseBasePath}/${record?.releaseId}/overview`)}
          />
        </KoTooltips>
        <KoTooltips title='Edit'>
          <KoButton
            className='btn'
            iconBtn={true}
            icon={<KoIcon width={14} height={14} name='EditOutlined' />}
            onClick={(e) => { e.stopPropagation(); setCurrentRecord(record); setModal(true); }}
          />
        </KoTooltips>
        <KoTooltips title='Delete'>
          <KoButton
            className='btn'
            iconBtn={true}
            icon={<KoIcon width={14} height={14} name='DeleteOutlined' />}
            onClick={(e) => { e.stopPropagation(); setCurrentRecord(record); setDeleteModal(true); }}
          />
        </KoTooltips>
      </div>
    )
  }];

  const enrichedColumns = COLUMNS_RELEASES.map((col) => {
    const key = col.id || col.key;

    if (key === 'stage') {
      return {
        ...col,
        render: (val) => {
          const cfg = LIFECYCLE_STAGE_MAP[val] || { label: val, color: 'default' };
          return <KoTag color={cfg.color}>{cfg.label}</KoTag>;
        }
      };
    }
    if (key === 'classification') {
      return {
        ...col,
        render: (val) => {
          const cfg = getClassificationConfig(val);
          return <KoTag color={cfg.color}>{cfg.label}</KoTag>;
        }
      };
    }
    if (key === 'riskLevel') {
      return {
        ...col,
        render: (val) => {
          if (!val) return <KoText text='-' />;
          const cfg = getRiskConfig(val);
          return <KoTag color={cfg.color}>{cfg.label}</KoTag>;
        }
      };
    }
    if (key === 'targetDate') {
      return { ...col, render: (val) => val ? <KoDateFormatter data={val} /> : <KoText text='-' /> };
    }
    if (key === 'qaStatus' || key === 'uatStatus') {
      return {
        ...col,
        render: (val) => val
          ? <KoTag color={val === 'passed' || val === 'signed_off' ? 'success' : val === 'failed' || val === 'rejected' ? 'error' : 'warning'}>{val}</KoTag>
          : <KoText text='-' />
      };
    }
    if (key === 'environment') {
      return {
        ...col,
        render: (val) => val ? <KoTag color='blue'>{val}</KoTag> : <KoText text='-' />
      };
    }
    if (key === 'participatingTeams') {
      return {
        ...col,
        widget: 'custom',
        customRender: (data, record) => {
          const arr = data || record?.participatingTeams || [];
          if (!arr?.length) return <KoText text='-' />;
          return (
            <div className={styles.teamTags}>
              {arr.slice(0, 2).map((t) => (
                <KoTag key={t.team} color='blue'>{getTeamLabel(t.team)}</KoTag>
              ))}
              {arr.length > 2 && (
                <KoTag color='default'>+{arr.length - 2}</KoTag>
              )}
            </div>
          );
        }
      };
    }
    return col;
  });

  return (
    <div className='tableCard'>
      <KoToolBar
        title='Releases'
        search={{
          visible: true,
          onChange: (e) => onSearchText(e),
          placeholder: 'Search by version, name, or owner'
        }}
        filter={{ visible: false, options: [] }}
        buttonList={[
          {
            label: 'New Release',
            type: 'primary',
            icon: <KoIcon color='#FFF' name='PlusOutlined' width={18} height={18} />,
            onClick: () => { setCurrentRecord(); setModal(true); }
          }
        ]}
      />

      <KoTable
        rowKey='releaseId'
        onRowSelection={(rows) => setSelectedRows(Array.isArray(rows) ? rows : [])}
        data={{ columns: enrichedColumns, rows: rowList }}
        extraColumnsLeft={columnsLeft}
        extraColumnsRight={columnsRight}
        maxHeight={100}
      />

      <KoPagination
        current={parseInt(pagination.page) || 1}
        total={totalCount}
        showTotal={(total, range) => `Displaying records ${range[0]}-${range[1]} of ${total}`}
        pageSize={pagination.size}
        defaultCurrent={pagination.page}
        onChange={onPagination}
        showQuickJumper={false}
        simple={false}
        responsive={false}
      />

      {/* Create / Edit Modal */}
      <KoModal
        title={currentRecord?.releaseId
          ? `Update Release ${currentRecord?.version?.toUpperCase()}`
          : 'Create New Release'}
        centered
        open={isOpen}
        afterClose={() => setCurrentRecord()}
        handleOk={() => { setCurrentRecord(); setModal(false); }}
        onClose={() => { setCurrentRecord(); setModal(false); }}
        width={'min(900px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => setModal(false)} />,
          <KoButton
            key='submit'
            type='primary'
            label={currentRecord?.releaseId ? 'Update' : 'Create'}
            onClick={() => form.submit()}
          />
        ]}
      >
        <ReleaseCrud
          form={form}
          initialValues={currentRecord}
          handleFinish={handleFinish}
        />
      </KoModal>

      {/* Delete Confirm Modal */}
      <KoModal
        title={`Delete Release ${currentRecord?.version?.toUpperCase() || ''}`}
        centered
        open={isOpenDelete}
        afterClose={() => setCurrentRecord()}
        handleOk={() => { setCurrentRecord(); setDeleteModal(false); }}
        onClose={() => { setCurrentRecord(); setDeleteModal(false); }}
        width={520}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => setDeleteModal(false)} />,
          <KoButton
            key='delete'
            color='danger'
            variant='solid'
            type='primary'
            label='Delete'
            onClick={handleDelete}
          />
        ]}
      >
        <div className={styles.confirmBody}>
          <KoTitle level={5} text='Are you sure you want to delete this release?' />
          <KoText text='This action is irreversible. All linked PR, QA, UAT, and gate data will also be removed.' />
        </div>
      </KoModal>
    </div>
  );
}

export default ReleaseList;
